// deps
import React, { memo, RefObject, useCallback, useMemo } from "react";

// internal deps
import { PlanDetails, RenderingDetails } from "@swiggy-private/cart-types";
import {
    BillDetailItemDisplay,
    BillDetailItemInfoDisplay,
    BillDetailItemSeparator,
    BillDetailItemTitle,
    BillDetailItemTooltipDisplay,
    TransparentLine,
    CollapsableBillDetailsWrapper,
    BillDetailsBanner,
    TransparentLineV2,
    getBillDetails,
} from "@swiggy-private/core-ui";

// helper
import {
    isSuperItem,
    isItemValueRupee,
    isOneLiteItem,
    canShowTooltip,
    getTagFromDeliveryFeeKey,
    getValidDeliveryFeeKeys,
    VALID_RENDERING_DETAILS_TYPE,
    SavingsStamp,
    BillText,
    getFreeTextPrimitive,
} from "@swiggy-private/cart-ui";

// styles
import { BillDetailCartGroupContainer, BillDetailsBannerContainer } from "./BillDetailsRenderer.style";

// hooks
import { useBillDetailsRenderer } from "@cart-client/hooks/useBillDetailsRendererData";
import { useBillDetailsBannerAnalytics } from "@cart-client/hooks/useBillDetailsBannerAnalytics";

export interface IProps {
    renderingDetails: RenderingDetails[];
    setBillDetailsOneCardComms?: VoidFunction;
    planAddedRef: RefObject<HTMLDivElement>;
    planDetails: PlanDetails;
}

const BillDetailsRenderer = ({ renderingDetails, setBillDetailsOneCardComms, planAddedRef, planDetails }: IProps) => {
    const billDetails = useMemo(() => getBillDetails(renderingDetails), [renderingDetails]);
    const { onImpressionBanner, onClickBanner, onImpressionBannerHalfcard, onClickCloseBannerHalfcard } =
        useBillDetailsBannerAnalytics();

    const { displayData, tooltipDisplayData, titleData, collapsibleWidgetData, savingsStampData } =
        useBillDetailsRenderer(setBillDetailsOneCardComms, planDetails);

    const renderBillDetailItem = useCallback(
        (itemData: RenderingDetails) => {
            switch (itemData.type.toUpperCase()) {
                case VALID_RENDERING_DETAILS_TYPE.DISPLAY:
                    return (
                        <BillDetailItemDisplay
                            itemData={itemData}
                            planAddedRef={planAddedRef}
                            {...displayData}
                            isSuperItem={isSuperItem}
                            isItemValueRupee={isItemValueRupee}
                            isOneLiteItem={isOneLiteItem}
                        />
                    );

                case VALID_RENDERING_DETAILS_TYPE.TOOLTIPDISPLAY:
                    return (
                        <BillDetailItemTooltipDisplay
                            itemData={itemData}
                            planAddedRef={planAddedRef}
                            planDetails={planDetails}
                            canShowTooltip={canShowTooltip}
                            isSuperItem={isSuperItem}
                            isOneLiteItem={isOneLiteItem}
                            getTagFromDeliveryFeeKey={getTagFromDeliveryFeeKey}
                            isItemValueRupee={isItemValueRupee}
                            getValidDeliveryFeeKeys={getValidDeliveryFeeKeys}
                            {...tooltipDisplayData}
                        />
                    );

                case VALID_RENDERING_DETAILS_TYPE.INFODISPLAY:
                    return <BillDetailItemInfoDisplay itemData={itemData} />;

                case VALID_RENDERING_DETAILS_TYPE.SEPARATOR:
                    return <BillDetailItemSeparator />;

                case VALID_RENDERING_DETAILS_TYPE.TITLE:
                    return <BillDetailItemTitle itemData={itemData} {...titleData} />;

                case VALID_RENDERING_DETAILS_TYPE.TRANSPARENT_SEPARATOR:
                    return <TransparentLine />;

                case VALID_RENDERING_DETAILS_TYPE.BANNER_WIDGET:
                    return (
                        <BillDetailsBannerContainer>
                            <BillDetailsBanner
                                bannerType={itemData?.key}
                                title={itemData?.meta?.title}
                                subtitle={itemData?.meta?.subtitle}
                                icon={itemData?.meta?.icon}
                                titleColor={itemData?.meta?.titleColor}
                                subtitleColor={itemData?.meta?.subtitleColor}
                                bannerBgColor={itemData?.meta?.bannerBgColor}
                                halfCardData={itemData?.meta?.halfCardData}
                                analytics={{
                                    onImpressionBanner,
                                    onClickBanner,
                                    onImpressionBannerHalfcard,
                                    onClickCloseBannerHalfcard,
                                }}
                            />
                        </BillDetailsBannerContainer>
                    );
                case VALID_RENDERING_DETAILS_TYPE.FREE_TEXT:
                    return <BillText item={getFreeTextPrimitive(itemData)} />;
                // this key signifies the end of the bill details section, if it's not present grand_total will be used as the end key
                case VALID_RENDERING_DETAILS_TYPE.BILL_DETAILS_SECTION_END:
                    return null;
                default:
                    return null;
            }
        },
        [
            displayData,
            onClickBanner,
            onClickCloseBannerHalfcard,
            onImpressionBanner,
            onImpressionBannerHalfcard,
            planAddedRef,
            planDetails,
            titleData,
            tooltipDisplayData,
        ],
    );

    return (
        <>
            <TransparentLineV2 />
            <div id="bill-details-container">
                <BillDetailCartGroupContainer isCollapsible={!collapsibleWidgetData.doNotShowCollapsableBill}>
                    <CollapsableBillDetailsWrapper {...collapsibleWidgetData}>
                        {billDetails?.children?.map((subItem: RenderingDetails) => renderBillDetailItem(subItem))}
                        <SavingsStamp {...savingsStampData} />
                    </CollapsableBillDetailsWrapper>
                </BillDetailCartGroupContainer>
            </div>
        </>
    );
};

export default memo(BillDetailsRenderer);
